import React, { useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const TabbingStringing = ({ formData, onFormDataChange }) => {
    const { tabbingStringingData = {}, observations = {}, nonConformance = '' } = formData;

    useEffect(() => {
        // Destructure formData to get initial values
        const { tabbingStringingData = {}, observations = {}, nonConformance = '' } = formData;
    
        // Check if formData already has prefilled data
        const hasFormData =
            Object.keys(tabbingStringingData).length > 0 ||
            Object.keys(observations).length > 0 ||
            nonConformance.trim() !== '';
    
        if (!hasFormData) {
            // If no formData, check metadata and inspectionData
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));
    
            let prefilledTabbingStringingData = {};
            let prefilledObservations = {};
            let prefilledNonConformance = '';
    
            // Check metadata for prefilled data
            if (metadata && metadata.pre && metadata.pre.TabbingStringing_pre) {
                prefilledTabbingStringingData = metadata.pre.TabbingStringing_pre;
                prefilledObservations = {
                    ribbonMisalignment: prefilledTabbingStringingData.ribbonMisalignment.map(() => ({
                        observation1: '',
                        observation2: ''
                    })),
                    poorSoldering: prefilledTabbingStringingData.poorSoldering.map(() => ({
                        observation1: '',
                        observation2: ''
                    })),
                    soldering: prefilledTabbingStringingData.soldering.map(() => ({
                        observation1: '',
                        observation2: ''
                    })),
                    string: prefilledTabbingStringingData.string.map(() => ({
                        observation1: '',
                        observation2: ''
                    })),
                    rejectStrings: prefilledTabbingStringingData.rejectStrings.map(() => ({
                        observation1: '',
                        observation2: ''
                    }))
                };
            }
    
            // Check inspectionData for prefilled data (overrides metadata if present)
            if (
                inspectionData &&
                inspectionData.PreLamination &&
                inspectionData.PreLamination.TabbingStringing_pre
            ) {
                const { TabbingStringing, nonConformance: inspectionNonConformance } =
                    inspectionData.PreLamination.TabbingStringing_pre;
    
                prefilledTabbingStringingData = TabbingStringing;
                prefilledObservations = {
                    ribbonMisalignment: TabbingStringing.ribbonMisalignment.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || ''
                    })),
                    poorSoldering: TabbingStringing.poorSoldering.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || ''
                    })),
                    soldering: TabbingStringing.soldering.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || ''
                    })),
                    string: TabbingStringing.string.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || ''
                    })),
                    rejectStrings: TabbingStringing.rejectStrings.map(item => ({
                        observation1: item.observation1 || '',
                        observation2: item.observation2 || ''
                    }))
                };
                prefilledNonConformance = inspectionNonConformance || '';
            }
    
            // Update formData only if prefilled data exists
            if (
                Object.keys(prefilledTabbingStringingData).length > 0 ||
                Object.keys(prefilledObservations).length > 0 ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    tabbingStringingData: prefilledTabbingStringingData,
                    observations: prefilledObservations,
                    nonConformance: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);
    
    const handleObservationChange = (table, index, field, value) => {
        const updatedObservations = { ...observations };
        updatedObservations[table][index][field] = value;
        onFormDataChange({ ...formData, observations: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const sendDataToAPI = async () => {
        const incompleteObservation = Object.values(observations).some((table) =>
            table.some(obs => !obs.observation1.trim())
        );
        if (incompleteObservation) {
            alert('Please fill all Observation 1 fields before submitting.');
            return;
        }

        try {
            const projectId = localStorage.getItem('selectedProject');
            const inspectionDate = sessionStorage.getItem('inspectionDate');
            const inspectionShift = sessionStorage.getItem('inspectionShift');
            const inspectorName = sessionStorage.getItem('username');
            const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

            // Combine tabbingStringingData with observations
            const combinedData = {
                ribbonMisalignment: tabbingStringingData.ribbonMisalignment.map((item, index) => ({
                    ...item,
                    observation1: observations.ribbonMisalignment[index]?.observation1 || '',
                    observation2: observations.ribbonMisalignment[index]?.observation2 || ''
                })),
                poorSoldering: tabbingStringingData.poorSoldering.map((item, index) => ({
                    ...item,
                    observation1: observations.poorSoldering[index]?.observation1 || '',
                    observation2: observations.poorSoldering[index]?.observation2 || ''
                })),
                soldering: tabbingStringingData.soldering.map((item, index) => ({
                    ...item,
                    observation1: observations.soldering[index]?.observation1 || '',
                    observation2: observations.soldering[index]?.observation2 || ''
                })),
                string: tabbingStringingData.string.map((item, index) => ({
                    ...item,
                    observation1: observations.string[index]?.observation1 || '',
                    observation2: observations.string[index]?.observation2 || ''
                })),
                rejectStrings: tabbingStringingData.rejectStrings.map((item, index) => ({
                    ...item,
                    observation1: observations.rejectStrings[index]?.observation1 || '',
                    observation2: observations.rejectStrings[index]?.observation2 || ''
                }))
            };

            // Construct the payload in the desired format
            const payload = {
                projectId,
                inspectionSupplier,
                inspectionDate,
                inspectionShift,
                inspectorName,
                status: 'completed',
                PreLamination: {
                    TabbingStringing_pre: { TabbingStringing: combinedData, nonConformance }
                },
            };

            await submitFormData(payload);

            alert('Data submitted successfully');
        } catch (error) {
            console.error('Error submitting data:', error);
            alert('Error submitting data');
        }
    };

    const renderTable = (tableName, title) => (
        <div className="mt-6">
            <h4 className="text-lg font-semibold mb-2 text-gray-800">{title}</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tabbingStringingData[tableName]?.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                            >
                                <td className="py-3 px-4">{item.product}</td>
                                <td className="py-3 px-4">{item.specifications}</td>
                                <td className="py-3 px-4">{item.frequency}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations[tableName] ? observations[tableName][index].observation1 : ''}
                                        onChange={(e) =>
                                            handleObservationChange(tableName, index, 'observation1', e.target.value)
                                        }
                                        placeholder="Enter observation 1"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={observations[tableName] ? observations[tableName][index].observation2 : ''}
                                        onChange={(e) =>
                                            handleObservationChange(tableName, index, 'observation2', e.target.value)
                                        }
                                        placeholder="Enter observation 2"
                                        className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Tabbing & Stringing Inspection</h3>
            {renderTable('ribbonMisalignment', 'Ribbon Misalignment')}
            {renderTable('poorSoldering', 'Poor Soldering')}
            {renderTable('soldering', 'Soldering Temperature')}
            {renderTable('string', 'String')}
            {renderTable('rejectStrings', 'Reject Strings')}

            <div className="mt-6">
                <label className="block text-gray-700 font-semibold mb-2" htmlFor="nonConformance">
                    Non Conformance
                </label>
                <textarea
                    id="nonConformance"
                    value={nonConformance}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter non-conformance details"
                    className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                    rows="4"
                ></textarea>
            </div>

            <div className="mt-6">
                <button
                    onClick={sendDataToAPI}
                    className="bg-[#424752] text-white py-2 px-4 rounded-lg hover:bg-gray-500 transition-all duration-150"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default TabbingStringing;